import { useState, useEffect } from "react";
import { CreditCard, User, Plus, Pencil } from "lucide-react";
import Modal from "./common/Modal";
import AddEditCounterForm from "./forms/AddEditCounterForm";
import { useGetAllCounters } from "../service/counterService";
import { useGetAllBranches } from "../service/branchService";
import { Counter } from "../types/counterTypes";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../utils/components/ui/select";

const getRole = () => (localStorage.getItem("role") ?? "").toLowerCase();
const isAdmin = () => getRole() === "admin";

const CounterList = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCounter, setSelectedCounter] = useState<Counter | undefined>(
    undefined,
  );

  // For admin: branch selector state, defaulting to null (no selection yet)
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);

  // For non-admins: branch comes from localStorage
  const userBranchId = Number(localStorage.getItem("branchId") ?? 1);

  const activeBranchId = isAdmin() ? selectedBranchId : userBranchId;

  // Only needed for admin branch dropdown
  const { data: branchesData } = useGetAllBranches({
    params: {
      search: "",
      isActive: true,
      page: 0,
      size: 100,
      sortBy: "branchName",
      sortDir: "asc",
    },
  });

  // Set selectedBranchId to first branch when branches data loads
  useEffect(() => {
    if (
      isAdmin() &&
      branchesData?.content &&
      branchesData.content.length > 0 &&
      !selectedBranchId
    ) {
      setSelectedBranchId(branchesData.content[0].branchId);
    }
  }, [branchesData]);

  const { data: counterData } = useGetAllCounters({
    params: {
      search: "",
      page: 0,
      size: 10,
      branchId: activeBranchId ?? 0,
    },
  });

  const handleAddClick = () => {
    setSelectedCounter(undefined);
    setIsModalOpen(true);
  };

  const handleEditClick = (counter: Counter, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedCounter(counter);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedCounter(undefined);
  };

  return (
    <>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-[#21314A]">Counters</h2>
            <p className="text-sm text-[#8A9BB5] mt-0.5">
              Manage your counters across branches
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Branch filter — admin only */}
            {isAdmin() && (
              <Select
                value={selectedBranchId ? String(selectedBranchId) : undefined}
                onValueChange={(v) => setSelectedBranchId(Number(v))}
              >
                <SelectTrigger className="w-44 h-11 rounded-[12px] border-[#E8EEF9] text-sm">
                  <SelectValue placeholder="Select branch" />
                </SelectTrigger>
                <SelectContent>
                  {branchesData?.content?.map((branch) => (
                    <SelectItem
                      key={branch.branchId}
                      value={String(branch.branchId)}
                    >
                      {branch.branchName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* <button
              onClick={handleAddClick}
              className="h-11 rounded-[12px] bg-[#1A5EF9] px-5 text-white font-medium text-sm flex items-center gap-2 hover:bg-[#0E4FDE] transition-colors"
            >
              <Plus size={16} />
              Add Counter
            </button> */}
          </div>
        </div>

        {/* No branch selected prompt (admin only) */}
        {isAdmin() && !selectedBranchId ? (
          <div className="rounded-[20px] border border-[#E8EEF9] bg-white py-16 text-center">
            <CreditCard className="w-10 h-10 text-[#D0DAEE] mx-auto mb-3" />
            <p className="text-sm font-medium text-[#52627C]">
              Select a branch to view its counters
            </p>
          </div>
        ) : counterData?.content && counterData.content.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {counterData.content.map((counter) => (
              <div
                key={counter.id}
                className="rounded-[20px] border border-[#E8EEF9] bg-white shadow-[0_8px_24px_rgba(26,94,249,0.06)] p-5 hover:shadow-[0_12px_32px_rgba(26,94,249,0.12)] hover:border-[#BFD1FF] transition-all duration-200 cursor-pointer"
              >
                {/* Icon + status + edit */}
                <div className="flex items-start justify-between mb-4">
                  <div className="w-11 h-11 rounded-[12px] bg-[#F0F4FF] flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-[#1A5EF9]" />
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[11px] font-semibold px-2.5 py-1 rounded-full uppercase tracking-wide ${
                        counter.isActive
                          ? "bg-[#E6F9F0] text-[#1A9E5C]"
                          : "bg-[#FFF0F0] text-[#E03E3E]"
                      }`}
                    >
                      {counter.isActive ? "Active" : "Inactive"}
                    </span>
                    <button
                      onClick={(e) => handleEditClick(counter, e)}
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-[#8A9BB5] hover:bg-[#F0F4FF] hover:text-[#1A5EF9] transition-colors"
                      title="Edit counter"
                      aria-label="Edit counter"
                    >
                      <Pencil size={14} />
                    </button>
                  </div>
                </div>

                {/* Counter name */}
                <h3 className="text-[15px] font-semibold text-[#1A2B4A] mb-2 truncate">
                  {counter.counterName}
                </h3>

                {/* Status info */}
                <div className="flex items-center gap-1.5 text-[#8A9BB5] text-sm">
                  <User size={13} className="flex-shrink-0" />
                  <span className="truncate">
                    {counter.isActive ? "In use" : "Available"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-[20px] border border-[#E8EEF9] bg-white py-16 text-center">
            <CreditCard className="w-10 h-10 text-[#D0DAEE] mx-auto mb-3" />
            <p className="text-sm font-medium text-[#52627C]">
              No counters added yet
            </p>
            <p className="text-xs text-[#8A9BB5] mt-1">
              Click "Add Counter" to get started
            </p>
          </div>
        )}
      </div>

      <Modal open={isModalOpen} setOpen={setIsModalOpen}>
        <AddEditCounterForm
          onClose={handleModalClose}
          editCounter={selectedCounter}
        />
      </Modal>
    </>
  );
};

export default CounterList;
